import { useState, useRef, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { AssistantRuntimeProvider, useExternalStoreRuntime } from '@assistant-ui/react';
import type { AppendMessage, ThreadMessageLike } from '@assistant-ui/react';
import { sendChatMessage } from '../../../services/api';
import type { ResponseMode } from '../../../services/api';
import { MinervaContext } from './MinervaContext';
import type { ExtendedMessage } from './MinervaContext';

const STREAM_CHARS_PER_TICK = 8;
const STREAM_TICK_MS = 10;

interface MinervaRuntimeProviderProps {
  subject: string;
  responseMode: ResponseMode;
  children: ReactNode;
}

export function MinervaRuntimeProvider({ subject, responseMode, children }: MinervaRuntimeProviderProps) {
  const [extMessages, setExtMessages] = useState<ExtendedMessage[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const threadIdRef = useRef<string | null>(null);
  // Persistimos un user_id sencillo por sesión para el backend.
  const userIdRef = useRef<string>(`web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  // Reset on subject change
  useEffect(() => {
    setExtMessages([]);
    setIsRunning(false);
    threadIdRef.current = null;
  }, [subject]);

  // Streaming animation tick — reveals STREAM_CHARS_PER_TICK characters per interval
  useEffect(() => {
    const streamingMsg = extMessages.find(m => m.isStreaming);
    if (!streamingMsg) return;

    const current = streamingMsg.displayedContent.length;
    const total = streamingMsg.fullContent.length;

    if (current < total) {
      const timer = setTimeout(() => {
        setExtMessages(prev =>
          prev.map(m =>
            m.id === streamingMsg.id
              ? { ...m, displayedContent: m.fullContent.slice(0, current + STREAM_CHARS_PER_TICK) }
              : m
          )
        );
      }, STREAM_TICK_MS);
      return () => clearTimeout(timer);
    } else {
      setExtMessages(prev =>
        prev.map(m => (m.id === streamingMsg.id ? { ...m, isStreaming: false } : m))
      );
    }
  }, [extMessages]);

  const toThreadMessage = (msg: ExtendedMessage): ThreadMessageLike => ({
    role: msg.role,
    id: msg.id,
    content: [{ type: 'text', text: msg.isStreaming ? msg.displayedContent : msg.fullContent }],
  });

  // Lógica central de envío, reutilizada por el composer (onNew) y por los
  // chips de follow-up (ask).
  const runMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      const userId = `user-${Date.now()}`;
      setExtMessages(prev => [
        ...prev,
        { id: userId, role: 'user', fullContent: trimmed, displayedContent: trimmed, isStreaming: false },
      ]);

      setIsRunning(true);
      let newMsg: ExtendedMessage | null = null;

      try {
        const response = await sendChatMessage(
          trimmed,
          subject,
          threadIdRef.current,
          responseMode,
          userIdRef.current
        );
        if (response.thread_id?.trim()) {
          threadIdRef.current = response.thread_id.trim();
        }
        newMsg = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          fullContent: response.answer,
          displayedContent: '',
          citations: response.citations,
          followUpQuestions: response.follow_up_questions,
          confidence: response.confidence,
          reasoningQuality: response.reasoning_quality,
          queryId: response.query_id,
          isStreaming: true,
        };
      } catch {
        newMsg = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          fullContent:
            'Lo siento, ha ocurrido un error al procesar tu pregunta. Por favor, verifica que el servidor esté funcionando e intenta nuevamente.',
          displayedContent: '',
          isStreaming: true,
        };
      } finally {
        setIsRunning(false);
        if (newMsg) {
          const msg = newMsg;
          setExtMessages(prev => [...prev, msg]);
        }
      }
    },
    [subject, responseMode]
  );

  const onNew = async (message: AppendMessage) => {
    const part = message.content[0];
    if (!part || part.type !== 'text') return;
    await runMessage(part.text);
  };

  const ask = useCallback(
    (text: string) => {
      void runMessage(text);
    },
    [runMessage]
  );

  const runtime = useExternalStoreRuntime({
    messages: extMessages.map(toThreadMessage),
    isRunning,
    onNew,
    // messages are already ThreadMessageLike — identity converter satisfies the generic constraint
    convertMessage: (m: ThreadMessageLike) => m,
  });

  return (
    <MinervaContext.Provider value={{ extMessages, subject, ask }}>
      <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>
    </MinervaContext.Provider>
  );
}
